import type { BiometryCurves } from "@/types/biometry"
import type { CrlMeasurement, WeekPercentiles } from "@/types/pregnancy"
import { acceptHMRUpdate, defineStore } from "pinia"
import { useStaticStore } from "./static"

export const usePregnancyStore = defineStore("pregnancy", {
	state: () => ({
		pregnancyDays: 280,
		pregnancyMaxDays: 294,
		pregnancyWeeks: 40,
		pregnancyMaxWeeks: 42,
		conceptionDays: 14,
		minGestationalAgeDifference: -14,
		maxGestationalAgeDifference: 14
	}),
	getters: {
		fetalWeightPercentiles(): WeekPercentiles[] {
			const staticStore = useStaticStore()
			return staticStore.fetalWeight
		},
		motherWeightGain(): WeekPercentiles[] {
			const staticStore = useStaticStore()
			return staticStore.motherWeightGain
		},
		gestationalAgeCRL(): CrlMeasurement[] {
			const staticStore = useStaticStore()
			return staticStore.gestationalAgeCRL
		},
		gestationalAgeCRLRangeWeeks(): { minWeek: number; maxWeek: number } | null {
			const gestationalAgeCRL = this.gestationalAgeCRL

			if (!gestationalAgeCRL.length) return null

			const firstItem = gestationalAgeCRL[0]
			const lastItem = gestationalAgeCRL.at(-1)

			if (!firstItem || !lastItem) return null

			return {
				minWeek: Math.floor(firstItem.days / 7) + 1,
				maxWeek: Math.floor(lastItem.days / 7) + 1
			}
		},
		biometryCurves(): BiometryCurves {
			const staticStore = useStaticStore()
			return staticStore.biometryCurves
		},
		gestationalAgeBiometryRangeWeeks(): { minWeek: number; maxWeek: number } | null {
			const curves = Object.values(this.biometryCurves)

			if (!curves.length) return null

			let minWeek = Number.POSITIVE_INFINITY
			let maxWeek = Number.NEGATIVE_INFINITY

			for (const series of curves) {
				if (!series?.length) continue

				const firstItem = series[0]
				const lastItem = series.at(-1)

				if (firstItem) minWeek = Math.min(minWeek, firstItem.week)
				if (lastItem) maxWeek = Math.max(maxWeek, lastItem.week)
			}

			if (!Number.isFinite(minWeek) || !Number.isFinite(maxWeek)) return null

			return { minWeek, maxWeek }
		}
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(usePregnancyStore, import.meta.hot))
}
